import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ShieldCheck, BadgeCheck, ClipboardList, ArrowRightLeft, Check, ExternalLink,
  CircleAlert, Timer, Plus, MessageSquare, ShieldAlert, Gavel, Save, ArrowRight,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import FixtureChat from "@/components/FixtureChat";

function LandingCard({ label, count, tint, borderTint, icon: Icon, href, testId, children, cta }) {
  return (
    <a
      href={href}
      data-testid={testId}
      className={`block border ${borderTint} bg-[#0a0a12] hover:bg-[#101018] p-5 transition-colors group`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${tint}`} />
          <div className="text-[10px] font-mono uppercase tracking-widest text-gray-400">
            {label}
          </div>
        </div>
        <ArrowRight className={`w-4 h-4 ${tint} opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all`} />
      </div>
      <div className={`font-heading font-black text-5xl leading-none mt-3 ${tint}`}>{count}</div>
      <div className="mt-3 space-y-1 min-h-[56px]">
        {children}
      </div>
      {cta && (
        <div className={`text-[10px] font-mono uppercase tracking-widest mt-3 pt-3 border-t border-[#232330] ${tint}`}>
          {cta} →
        </div>
      )}
    </a>
  );
}

function StatCard({ label, value, tint }) {
  return (
    <div className="border border-[#232330] bg-[#101015] p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`font-heading font-black text-3xl mt-1 ${tint}`}>{value}</div>
    </div>
  );
}

export default function Officials() {
  const { user } = useAuth();
  const allowed = user?.role === "admin" || user?.role === "official";
  const [players, setPlayers] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [teams, setTeams] = useState([]);
  const [scoreFor, setScoreFor] = useState(null);
  const [hs, setHs] = useState(0);
  const [aScore, setAScore] = useState(0);
  const [transferOpen, setTransferOpen] = useState(false);
  const [tForm, setTForm] = useState({ player_id: "", from_team_id: "", to_team_id: "", status: "Confirmed" });

  const [disputes, setDisputes] = useState([]);
  const [chatFor, setChatFor] = useState(null);
  const [respondFor, setRespondFor] = useState(null);
  const [respondText, setRespondText] = useState("");
  const [respondStatus, setRespondStatus] = useState("reviewing");
  // Iter27 — Officials Ruling Panel
  const [ruleFor, setRuleFor] = useState(null);
  const [ruleHome, setRuleHome] = useState(0);
  const [ruleAway, setRuleAway] = useState(0);
  const [ruleNote, setRuleNote] = useState("");

  const load = async () => {
    if (!allowed) return;
    const [pl, fx, tm, dp] = await Promise.all([
      api.get("/players"),
      api.get("/fixtures"),
      api.get("/teams"),
      api.get("/disputes").catch(() => ({ data: [] })),
    ]);
    setPlayers(pl.data);
    setFixtures(fx.data);
    setTeams(tm.data);
    setDisputes(dp.data || []);
  };
  useEffect(() => { if (allowed) load(); }, [allowed]);

  const teamById = (id) => teams.find((t) => t.id === id);
  const unverified = useMemo(() => players.filter((p) => !p.hll_verified), [players]);
  const nowTs = Date.now();
  const overdue = useMemo(
    () => fixtures
      .filter((f) => (f.status === "live") || (f.status === "upcoming" && new Date(f.scheduled_at).getTime() < nowTs))
      .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || "")),
    [fixtures, nowTs]
  );
  const upcomingSoon = useMemo(
    () => fixtures
      .filter((f) => f.status === "upcoming" && new Date(f.scheduled_at).getTime() >= nowTs)
      .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""))
      .slice(0, 8),
    [fixtures, nowTs]
  );
  const openDisputes = useMemo(() => disputes.filter((d) => d.status !== "resolved" && d.status !== "dismissed"), [disputes]);
  const pendingRulings = useMemo(
    () => disputes.filter(
      (d) => (d.category === "result" || d.category === "score_dispute")
        && d.status !== "resolved" && d.status !== "dismissed"
    ),
    [disputes]
  );

  if (!user) return <div className="text-gray-500">Loading…</div>;
  if (!allowed) return <Navigate to="/dashboard" replace />;

  const verifyPlayer = async (id) => {
    try {
      await api.post(`/admin/players/${id}/hll-verify`);
      toast.success("Player verified.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitScore = async () => {
    if (Number(hs) < 0 || Number(aScore) < 0) { toast.error("Scores must be positive."); return; }
    try {
      await api.post(`/fixtures/${scoreFor.id}/result`, {
        home_score: Number(hs), away_score: Number(aScore),
      });
      toast.success("Result submitted.");
      setScoreFor(null); setHs(0); setAScore(0);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitTransfer = async () => {
    if (!tForm.player_id || !tForm.to_team_id) { toast.error("Pick a player and destination team."); return; }
    try {
      await api.post("/transfers", { ...tForm, fee: "Undisclosed", from_team_id: tForm.from_team_id || null });
      toast.success("Transfer logged.");
      setTransferOpen(false);
      setTForm({ player_id: "", from_team_id: "", to_team_id: "", status: "Confirmed" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitRuling = async () => {
    if (!ruleFor) return;
    if (Number(ruleHome) < 0 || Number(ruleAway) < 0) { toast.error("Scores must be positive."); return; }
    try {
      const { data } = await api.post(`/disputes/${ruleFor.id}/rule`, {
        home_score: Number(ruleHome),
        away_score: Number(ruleAway),
        ruling_note: ruleNote || undefined,
      });
      toast.success(`Ruling locked · ${data.final.home}–${data.final.away}. Dispute resolved.`);
      setRuleFor(null); setRuleHome(0); setRuleAway(0); setRuleNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="officials-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[#B084FF] font-mono tracking-[0.3em] text-xs mb-2 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> / OFFICIALS PORTAL
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">
            League Officials
          </h1>
          <p className="text-gray-400 mt-2 max-w-xl text-sm">
            Clear the backlog — verify rosters, close out unresolved fixtures, and log confirmed transfers.
          </p>
        </div>
        <Button
          onClick={() => setTransferOpen(true)}
          data-testid="officials-new-transfer"
          className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold"
        >
          <Plus className="w-4 h-4 mr-2" /> Log transfer
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="officials-landing-grid">
        <LandingCard
          label="Open disputes"
          count={openDisputes.length}
          tint="text-[#FF007F]"
          borderTint="border-[#FF007F]/40 hover:border-[#FF007F]"
          icon={ShieldAlert}
          href="#disputes"
          testId="landing-disputes"
          cta="Jump to inbox"
        >
          {openDisputes.slice(0, 3).map((d) => {
            const fx = fixtures.find((f) => f.id === d.fixture_id);
            const h = fx && teamById(fx.home_team_id);
            const a = fx && teamById(fx.away_team_id);
            return (
              <div key={d.id} className="text-[11px] font-mono truncate text-gray-300">
                <span className="text-[#FF007F]">·</span> {h?.tag || "TBD"} vs {a?.tag || "TBD"} · {d.title}
              </div>
            );
          })}
          {openDisputes.length === 0 && (
            <div className="text-[11px] font-mono text-gray-600">All quiet — no active disputes.</div>
          )}
        </LandingCard>

        <LandingCard
          label="Pending rulings"
          count={pendingRulings.length}
          tint="text-[#F4D03F]"
          borderTint="border-[#F4D03F]/40 hover:border-[#F4D03F]"
          icon={Gavel}
          href="#disputes"
          testId="landing-rulings"
          cta="Rule final scores"
        >
          {pendingRulings.slice(0, 3).map((d) => {
            const fx = fixtures.find((f) => f.id === d.fixture_id);
            const h = fx && teamById(fx.home_team_id);
            const a = fx && teamById(fx.away_team_id);
            const proposed = fx?.proposed_result;
            return (
              <div key={d.id} className="text-[11px] font-mono truncate text-gray-300">
                <span className="text-[#F4D03F]">·</span> {h?.tag || "TBD"} vs {a?.tag || "TBD"}
                {proposed && (
                  <span className="text-gray-500"> · reps: {proposed.home_score}–{proposed.away_score}</span>
                )}
              </div>
            );
          })}
          {pendingRulings.length === 0 && (
            <div className="text-[11px] font-mono text-gray-600">No score disputes awaiting a ruling.</div>
          )}
        </LandingCard>

        <LandingCard
          label="Unresolved fixtures"
          count={overdue.length}
          tint="text-red-400"
          borderTint="border-red-500/40 hover:border-red-500"
          icon={CircleAlert}
          href="#fixtures"
          testId="landing-fixtures"
          cta="Enter final scores"
        >
          {overdue.slice(0, 3).map((f) => {
            const h = teamById(f.home_team_id);
            const a = teamById(f.away_team_id);
            return (
              <div key={f.id} className="text-[11px] font-mono truncate text-gray-300">
                <span className={f.status === "live" ? "text-red-400" : "text-[#F4D03F]"}>
                  {f.status === "live" ? "LIVE" : "PAST"}
                </span>
                <span className="text-gray-500"> · </span>
                {h?.tag || "TBD"} vs {a?.tag || "TBD"}
              </div>
            );
          })}
          {overdue.length === 0 && (
            <div className="text-[11px] font-mono text-gray-600">Every past fixture has a final score.</div>
          )}
        </LandingCard>

        <LandingCard
          label="Verification backlog"
          count={unverified.length}
          tint="text-cyan-400"
          borderTint="border-cyan-400/40 hover:border-cyan-400"
          icon={BadgeCheck}
          href="#verifications"
          testId="landing-verifications"
          cta="Verify rosters"
        >
          {unverified.slice(0, 3).map((p) => {
            const team = teamById(p.team_id);
            return (
              <div key={p.id} className="text-[11px] font-mono truncate text-gray-300">
                <span className="text-cyan-400">·</span> {p.handle}
                <span className="text-gray-500"> · {team?.tag || "FA"}</span>
              </div>
            );
          })}
          {unverified.length === 0 && (
            <div className="text-[11px] font-mono text-gray-600">Every player is verified.</div>
          )}
        </LandingCard>
      </div>

      <div className="flex flex-wrap gap-2 border-t border-[#232330] pt-4" data-testid="officials-quick-nav">
        <Link
          to="/match"
          data-testid="officials-nav-match"
          className="text-[10px] font-mono uppercase tracking-widest text-[#B084FF] border border-[#232330] hover:border-[#B084FF] px-3 py-1.5"
        >
          Match Center →
        </Link>
        <Link
          to="/schedule"
          data-testid="officials-nav-schedule"
          className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 border border-[#232330] hover:border-cyan-400 px-3 py-1.5"
        >
          Schedule →
        </Link>
        <Link
          to="/trophies"
          data-testid="officials-nav-audit"
          className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] border border-[#232330] hover:border-[#F4D03F] px-3 py-1.5"
        >
          Ruling audit →
        </Link>
        <Link
          to="/teams"
          data-testid="officials-nav-teams"
          className="text-[10px] font-mono uppercase tracking-widest text-gray-300 border border-[#232330] hover:border-white px-3 py-1.5"
        >
          Teams →
        </Link>
      </div>

      {/* Disputes inbox */}
      <section id="disputes" data-testid="officials-disputes" className="space-y-3 scroll-mt-20">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-[#FF007F]" />
          <div className="font-heading text-xl font-black uppercase tracking-tight">Disputes Inbox</div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500 ml-2">
            {openDisputes.length} open · {disputes.length} total
          </span>
        </div>
        {disputes.length === 0 ? (
          <div className="border border-[#232330] p-6 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
            No disputes filed. All quiet on the front.
          </div>
        ) : (
          <div className="space-y-2">
            {disputes.map((d) => {
              const fx = fixtures.find((f) => f.id === d.fixture_id);
              const h = fx && teamById(fx.home_team_id);
              const a = fx && teamById(fx.away_team_id);
              const statusTint = {
                open: "border-[#FF007F] text-[#FF007F] bg-[#FF007F]/5",
                reviewing: "border-[#F4D03F] text-[#F4D03F] bg-[#F4D03F]/5",
                resolved: "border-cyan-400 text-cyan-400 bg-cyan-400/5",
                dismissed: "border-[#232330] text-gray-500 bg-transparent",
              }[d.status] || "border-[#232330]";
              return (
                <div key={d.id} data-testid={`dispute-row-${d.id}`} className={`border ${statusTint} p-4`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${statusTint}`}>
                          {d.status}
                        </span>
                        <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                          {d.category}
                        </span>
                        <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500 ml-2">
                          {new Date(d.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="font-heading font-black uppercase tracking-tight text-lg">
                        {d.title}
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-0.5">
                        Filed by {d.filed_by_name || d.filed_by_email} · {h?.tag || "TBD"} vs {a?.tag || "TBD"}
                      </div>
                      <p className="text-sm text-gray-300 mt-2 whitespace-pre-wrap">{d.body}</p>
                      {d.response && (
                        <div className="mt-3 border-l-2 border-cyan-400/60 pl-3">
                          <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400">Officials response</div>
                          <p className="text-sm text-gray-200 mt-1 whitespace-pre-wrap">{d.response}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 items-stretch">
                      {(d.category === "result" || d.category === "score_dispute") && d.status !== "resolved" && d.status !== "dismissed" && fx && (
                        <button
                          onClick={() => {
                            setRuleFor(d);
                            setRuleHome(fx.proposed_result?.home_score ?? fx.home_score ?? 0);
                            setRuleAway(fx.proposed_result?.away_score ?? fx.away_score ?? 0);
                            setRuleNote("");
                          }}
                          data-testid={`dispute-rule-${d.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-black bg-[#F4D03F] hover:bg-yellow-300 px-3 py-1.5 flex items-center gap-1 justify-center font-bold"
                        >
                          <Gavel className="w-3 h-3" /> Rule final score
                        </button>
                      )}
                      <button
                        onClick={() => { setRespondFor(d); setRespondText(d.response || ""); setRespondStatus(d.status || "reviewing"); }}
                        data-testid={`dispute-respond-${d.id}`}
                        className="text-[10px] font-mono uppercase tracking-widest text-white border border-[#B084FF] hover:bg-[#B084FF] hover:text-black px-3 py-1.5"
                      >
                        Respond
                      </button>
                      {fx && (
                        <button
                          onClick={() => setChatFor(fx)}
                          data-testid={`dispute-chat-${d.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-[#B084FF] border border-[#232330] hover:border-[#B084FF] px-3 py-1.5 flex items-center gap-1 justify-center"
                        >
                          <MessageSquare className="w-3 h-3" /> Chat
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Pending verifications */}
      <section id="verifications" data-testid="officials-verifications" className="space-y-3 scroll-mt-20">
        <div className="flex items-center gap-2">
          <BadgeCheck className="w-4 h-4 text-cyan-400" />
          <div className="font-heading text-xl font-black uppercase tracking-tight">Pending Verifications</div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500 ml-2">
            {unverified.length} to review
          </span>
        </div>
        {unverified.length === 0 ? (
          <div className="border border-[#232330] p-6 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
            All caught up — every player is verified.
          </div>
        ) : (
          <div className="border border-[#232330] overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-[#232330] bg-[#0a0a12]">
                  {["Handle", "Team", "HLL ID", "Rating", "Records", "Action"].map((h) => (
                    <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest text-gray-500 px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {unverified.map((p) => {
                  const team = teamById(p.team_id);
                  return (
                    <tr key={p.id} data-testid={`officials-verify-row-${p.handle}`} className="border-b border-[#232330] hover:bg-black/40">
                      <td className="px-4 py-3 font-heading font-bold uppercase tracking-tight">{p.handle}</td>
                      <td className="px-4 py-3 text-gray-400">{team?.tag || "FA"}</td>
                      <td className="px-4 py-3 font-mono text-[11px] text-gray-500 break-all">{p.hll_id || p.records_id || "—"}</td>
                      <td className="px-4 py-3 font-mono text-cyan-400">{(p.rating ?? 0).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        {p.hll_id ? (
                          <a
                            href={`https://hllrecords.com/${p.hll_id}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`officials-records-${p.handle}`}
                            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#B084FF] hover:text-purple-300"
                          >
                            HLL Records <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="text-[10px] font-mono uppercase tracking-widest text-gray-600">No ID</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => verifyPlayer(p.id)}
                          data-testid={`officials-verify-${p.handle}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400 hover:bg-cyan-400 hover:text-black px-3 py-1.5 flex items-center gap-1"
                        >
                          <Check className="w-3 h-3" /> Verify
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Unresolved fixtures */}
      <section id="fixtures" data-testid="officials-fixtures" className="space-y-3 scroll-mt-20">
        <div className="flex items-center gap-2">
          <CircleAlert className="w-4 h-4 text-red-500" />
          <div className="font-heading text-xl font-black uppercase tracking-tight">Unresolved Fixtures</div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500 ml-2">
            live or past kickoff with no score
          </span>
        </div>
        {overdue.length === 0 ? (
          <div className="border border-[#232330] p-6 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
            Every past fixture has a final score.
          </div>
        ) : (
          <div className="space-y-2">
            {overdue.map((f) => {
              const h = teamById(f.home_team_id);
              const a = teamById(f.away_team_id);
              const kickoff = new Date(f.scheduled_at);
              const late = kickoff.getTime() < nowTs;
              return (
                <div key={f.id} data-testid={`officials-fixture-${f.id}`} className="border border-red-500/40 bg-red-500/5 p-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Timer className={`w-4 h-4 ${late ? "text-red-400" : "text-[#F4D03F]"}`} />
                    <div className="min-w-0">
                      <div className="font-heading font-bold uppercase tracking-tight truncate">
                        {h?.name || "TBD"} <span className="text-gray-500">vs</span> {a?.name || "TBD"}
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
                        {f.game}{f.map ? " · " + f.map : ""} · {kickoff.toLocaleString()}
                        <span className={`ml-2 ${f.status === "live" ? "text-red-400" : "text-[#F4D03F]"}`}>
                          {f.status === "live" ? "LIVE" : "PAST"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/live/${f.id}`}
                      className="text-[10px] font-mono uppercase tracking-widest text-gray-400 hover:text-white border border-[#232330] hover:border-cyan-400 px-3 py-1.5"
                    >
                      Open live
                    </Link>
                    <button
                      onClick={() => {
                        setScoreFor(f);
                        setHs(f.home_score ?? 0);
                        setAScore(f.away_score ?? 0);
                      }}
                      data-testid={`officials-score-${f.id}`}
                      className="text-[10px] font-mono uppercase tracking-widest text-red-400 border border-red-500 hover:bg-red-500 hover:text-white px-3 py-1.5 flex items-center gap-1"
                    >
                      <ClipboardList className="w-3 h-3" /> Enter score
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Match chat modal */}
      <FixtureChat fixture={chatFor} teams={teams} open={!!chatFor} onClose={() => setChatFor(null)} />

      {/* Respond to dispute */}
      <Dialog open={!!respondFor} onOpenChange={(o) => !o && setRespondFor(null)}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              Respond · {respondFor?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Status</Label>
              <Select value={respondStatus} onValueChange={setRespondStatus}>
                <SelectTrigger data-testid="dispute-response-status" className="rounded-none bg-[#05050A] border-[#232330] mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#101015] border-[#232330] text-white">
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="reviewing">Reviewing</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="dismissed">Dismissed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Response</Label>
              <Textarea
                data-testid="dispute-response-text"
                rows={5}
                value={respondText}
                onChange={(e) => setRespondText(e.target.value)}
                placeholder="Explain the ruling. Reps will see this on their side."
                className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-sm"
              />
            </div>
            <Button
              onClick={async () => {
                try {
                  await api.put(`/disputes/${respondFor.id}`, { status: respondStatus, response: respondText });
                  toast.success("Dispute updated.");
                  setRespondFor(null);
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}
              data-testid="dispute-response-submit"
              className="rounded-none w-full bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold"
            >
              Publish response
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Score dialog */}
      <Dialog open={!!scoreFor} onOpenChange={(o) => !o && setScoreFor(null)}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              Enter final score · {teamById(scoreFor?.home_team_id)?.tag} vs {teamById(scoreFor?.away_team_id)?.tag}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">{teamById(scoreFor?.home_team_id)?.tag}</Label>
              <Input
                data-testid="officials-score-home"
                type="number" min={0} value={hs}
                onChange={(e) => setHs(e.target.value)}
                className="rounded-none bg-[#05050A] border-[#232330] font-mono text-2xl mt-2"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">{teamById(scoreFor?.away_team_id)?.tag}</Label>
              <Input
                data-testid="officials-score-away"
                type="number" min={0} value={aScore}
                onChange={(e) => setAScore(e.target.value)}
                className="rounded-none bg-[#05050A] border-[#232330] font-mono text-2xl mt-2"
              />
            </div>
          </div>
          <Button
            onClick={submitScore}
            data-testid="officials-score-submit"
            className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold mt-2"
          >
            Publish result
          </Button>
        </DialogContent>
      </Dialog>

      {/* Iter27 · Officials Ruling Panel — one-screen lock on disputed fixtures */}
      <Dialog open={!!ruleFor} onOpenChange={(o) => !o && setRuleFor(null)}>
        <DialogContent className="bg-[#101015] border-[#F4D03F] text-white rounded-none" data-testid="ruling-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2 text-[#F4D03F]">
              <Gavel className="w-4 h-4" /> Rule final score
            </DialogTitle>
          </DialogHeader>
          {ruleFor && (() => {
            const fx = fixtures.find((f) => f.id === ruleFor.fixture_id);
            const h = fx && teamById(fx.home_team_id);
            const a = fx && teamById(fx.away_team_id);
            const proposed = fx?.proposed_result;
            return (
              <div className="space-y-3 text-sm">
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-400">
                  {h?.tag || "HOME"} vs {a?.tag || "AWAY"} · Dispute {ruleFor.id.slice(0, 8)}
                </div>
                {proposed && (
                  <div className="border border-[#B084FF] bg-[#B084FF]/10 p-3 text-xs" data-testid="ruling-proposed">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#B084FF]">Reps proposed</div>
                    <div className="font-mono mt-1">{h?.tag} {proposed.home_score} — {proposed.away_score} {a?.tag} · by {proposed.submitter_side}</div>
                  </div>
                )}
                <p className="text-xs text-gray-400 font-mono uppercase tracking-widest">
                  Locking this score bypasses rep signatures, marks the fixture <span className="text-cyan-400">completed</span>, and resolves the dispute in one click.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-cyan-400">
                      {h?.tag || "HOME"}
                    </Label>
                    <Input
                      type="number" min={0}
                      value={ruleHome}
                      onChange={(e) => setRuleHome(e.target.value)}
                      data-testid="ruling-score-home"
                      className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-2xl h-16 text-center"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-[#FF007F]">
                      {a?.tag || "AWAY"}
                    </Label>
                    <Input
                      type="number" min={0}
                      value={ruleAway}
                      onChange={(e) => setRuleAway(e.target.value)}
                      data-testid="ruling-score-away"
                      className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-2xl h-16 text-center"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Ruling note (optional)</Label>
                  <Textarea
                    value={ruleNote}
                    onChange={(e) => setRuleNote(e.target.value)}
                    placeholder="e.g. Reviewed server logs — awarded to home."
                    data-testid="ruling-note"
                    className="mt-2 rounded-none bg-[#05050A] border-[#232330] font-mono text-xs min-h-20"
                  />
                </div>
                <Button
                  onClick={submitRuling}
                  data-testid="ruling-submit"
                  className="w-full rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold"
                >
                  <Save className="w-4 h-4 mr-2" /> Lock ruling
                </Button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Transfer dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">Log a transfer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Player</Label>
              <Select value={tForm.player_id} onValueChange={(v) => setTForm({ ...tForm, player_id: v })}>
                <SelectTrigger data-testid="officials-transfer-player" className="rounded-none bg-[#05050A] border-[#232330] mt-2">
                  <SelectValue placeholder="Pick a player" />
                </SelectTrigger>
                <SelectContent className="bg-[#101015] border-[#232330] text-white max-h-72">
                  {players.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.handle}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-400">From</Label>
                <Select value={tForm.from_team_id || "__fa__"} onValueChange={(v) => setTForm({ ...tForm, from_team_id: v === "__fa__" ? "" : v })}>
                  <SelectTrigger data-testid="officials-transfer-from" className="rounded-none bg-[#05050A] border-[#232330] mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#101015] border-[#232330] text-white max-h-72">
                    <SelectItem value="__fa__">Free Agent</SelectItem>
                    {teams.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.tag}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-400">To</Label>
                <Select value={tForm.to_team_id} onValueChange={(v) => setTForm({ ...tForm, to_team_id: v })}>
                  <SelectTrigger data-testid="officials-transfer-to" className="rounded-none bg-[#05050A] border-[#232330] mt-2">
                    <SelectValue placeholder="Pick destination" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#101015] border-[#232330] text-white max-h-72">
                    {teams.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.tag}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Status</Label>
              <Select value={tForm.status} onValueChange={(v) => setTForm({ ...tForm, status: v })}>
                <SelectTrigger data-testid="officials-transfer-status" className="rounded-none bg-[#05050A] border-[#232330] mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#101015] border-[#232330] text-white">
                  <SelectItem value="Confirmed">Confirmed</SelectItem>
                  <SelectItem value="Rumour">Rumour</SelectItem>
                  <SelectItem value="Loan">Loan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={submitTransfer}
              data-testid="officials-transfer-submit"
              className="rounded-none w-full bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold mt-2"
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" /> Log transfer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
